import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useDispatch, useSelector } from 'react-redux';
import { FC, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';

import {
  Document,
  Folder,
  Operation,
  Partner,
  TradingOffer,
  TradingOfferOperation,
} from 'types';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import successSwal from 'utils/successSwal';

import { UploadDocumentModalContextType } from '../types/UploadDocumentModalConextType';
import { uploadDocumentModalInitValue } from '../constants/uploadDocumentModalInitValue';
import { getLinkedTo } from '../utils/getLinkedTo';
import { updatePartnerDocuments } from '../utils/updatePartnerDocuments';
import { updateOperationDocuments } from '../utils/updateOperationDocuments';

const UploadDocumentModalContext =
  createContext<UploadDocumentModalContextType>(uploadDocumentModalInitValue);

type UploadDocumentModalProviderProps = {
  children: React.ReactNode; // eslint-disable-line
  partner?: Partner;
  operation?: Operation;
  tradingOffer?: TradingOffer;
  tradingOfferOperation?: TradingOfferOperation;
  folder?: Folder;
  onUploadDocument?: () => void;
};

export const UploadDocumentModalProvider: FC<
  UploadDocumentModalProviderProps
> = ({
  children,
  partner,
  operation,
  tradingOffer,
  tradingOfferOperation,
  folder,
  onUploadDocument,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [file, setFile] = useState<File | undefined>();
  const [name, setName] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleChangeFile = (newFile: File | undefined) => {
    setFile(newFile);

    if (newFile) {
      setName(newFile.name);
      setDate(new Date().toISOString().split('T')[0]);
    } else {
      setName('');
      setDate('');
    }
  };

  const handleChangeName = (newName: string) => {
    setName(newName);
  };

  const handleChangeDate = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    setDate(event.target.value);
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const createNewDocument = () => ({
    file,
    date,
    size: file?.size,
    name,
    author: user?._id,
    society: actualSociety?._id,
    fileType: fileTypes[file?.type as keyof typeof fileTypes],
    category: operation ? documentTypes.SCRIPTURES : documentTypes.DOCUMENTS,
    subcategory: '',
    description: t('DocumentLinkedManually'),
    linkedTo: getLinkedTo({
      partnerId: partner?._id,
      operationId: operation?._id,
      tradingOfferId: tradingOffer?._id,
      tradingOfferOperationId: tradingOfferOperation?._id,
    }),
    folder: folder?._id,
  });

  const handleDocumentUploadSuccess = async (uploadedDocument: Document) => {
    if (partner) {
      await updatePartnerDocuments({
        partner,
        newDocumentId: uploadedDocument?._id,
      });
    }

    if (operation) {
      await updateOperationDocuments({
        operation,
        newDocumentId: uploadedDocument?._id,
      });
    }
  };

  const handleUploadDocument = async () => {
    try {
      if (!file) {
        return;
      }

      setIsLoading(true);

      const newDocument = createNewDocument();

      const uploadedDocument = await store.dispatch(addDocument(newDocument));
      await handleDocumentUploadSuccess(uploadedDocument);

      successSwal(t('DocumentUploadedSuccessfully'));

      if (onUploadDocument) {
        onUploadDocument();
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    } finally {
      handleCloseModal();
    }
  };

  return (
    <UploadDocumentModalContext.Provider
      value={{
        data: {
          file,
          name,
          date,
          isLoading,
        },
        actions: {
          handleChangeFile,
          handleChangeName,
          handleChangeDate,
          handleUploadDocument,
          handleCloseModal,
        },
      }}
    >
      {children}
    </UploadDocumentModalContext.Provider>
  );
};

export const useUploadDocumentModal = () => {
  const context = useContext(UploadDocumentModalContext);
  if (!context) {
    throw new Error(
      'useUploadDocumentModal must be used within a UploadDocumentModalProvider'
    );
  }
  return context;
};
